import { useState } from 'react';
import { Button, Select, DatePicker, Space, Alert } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useStores } from '../hooks/useStores';
import { useAuth } from '../context/AuthContext';
import { fetchShipments } from '../api/shipments.api';

const { RangePicker } = DatePicker;

const EXPORT_COLUMNS = [
  { label: 'Order Number', getValue: (r: any) => r.orderNumber ?? '' },
  { label: 'SKU',              getValue: (_: any) => '' },
  { label: 'Item Description', getValue: (_: any) => '' },
  { label: 'Quantity',         getValue: (_: any) => '' },
  { label: 'ShipDate',         getValue: (r: any) => r.shipDate ? new Date(r.shipDate).toLocaleDateString('en-US') : '' },
  { label: 'Tracking #',       getValue: (r: any) => r.trackingNumber ?? '' },
];

function toCSV(rows: any[]): string {
  const header = EXPORT_COLUMNS.map(c => `"${c.label}"`).join(',');
  const lines = rows.map(row =>
    EXPORT_COLUMNS.map(c => `"${String(c.getValue(row)).replace(/"/g, '""')}"`).join(',')
  );
  return [header, ...lines].join('\n');
}

function downloadCSV(csv: string, filename: string) {
  const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export default function ExportReport() {
  const stores = useStores();
  const { user } = useAuth();

  const storeOptions = (user?.assignedStores && user.assignedStores.length > 0)
    ? stores
        .filter((s: any) => (user?.assignedStores ?? []).map((id: any) => String(id)).includes(String(s.storeId)))
        .map((s: any) => ({ label: s.storeName, value: s.storeId }))
    : stores.map((s: any) => ({ label: s.storeName, value: s.storeId }));

  const [storeId, setStoreId] = useState<string | undefined>(undefined);
  const [dateRange, setDateRange] = useState<[string | undefined, string | undefined]>([undefined, undefined]);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [exported, setExported] = useState<number | null>(null);

  async function handleExport() {
    setExporting(true);
    setError(null);
    setExported(null);
    try {
      const params: any = { page: 1, pageSize: 99999 };

      if (storeId) {
        params.storeId = storeId;
      } else if (user?.assignedStores && user.assignedStores.length > 0) {
        params.storeId = user.assignedStores.join(',');
      }

      if (dateRange[0]) params.shipDateStart = dateRange[0];
      if (dateRange[1]) params.shipDateEnd = dateRange[1];

      const data = await fetchShipments(params);
      const rows = data?.shipments || [];

      if (rows.length === 0) {
        setError('No data found for the selected filters.');
        return;
      }

      const csv = toCSV(rows);
      const from = dateRange[0] || 'all';
      const to = dateRange[1] || 'all';
      downloadCSV(csv, `shipments-${from}-to-${to}.csv`);
      setExported(rows.length);
    } catch {
      setError('Export failed. Please try again.');
    } finally {
      setExporting(false);
    }
  }

  return (
    <div style={{ maxWidth: 480, margin: '0 auto', paddingTop: 32 }}>
      <h2 style={{ marginBottom: 24 }}>Export Report</h2>

      <Space orientation="vertical" style={{ width: '100%' }} size="large">

        <div>
          <label style={{ display: 'block', marginBottom: 6, fontWeight: 500 }}>Store</label>
          <Select
            style={{ width: '100%' }}
            placeholder="All Stores"
            allowClear
            value={storeId}
            onChange={v => setStoreId(v)}
            options={storeOptions}
          />
        </div>

        <div>
          <label style={{ display: 'block', marginBottom: 6, fontWeight: 500 }}>Ship Date Range</label>
          <RangePicker
            style={{ width: '100%' }}
            value={[
              dateRange[0] ? dayjs(dateRange[0]) : null,
              dateRange[1] ? dayjs(dateRange[1]) : null
            ]}
            onChange={dates => setDateRange([
              dates?.[0]?.format('YYYY-MM-DD'),
              dates?.[1]?.format('YYYY-MM-DD')
            ])}
          />
        </div>

        {error && <Alert type="error" message={error} showIcon />}
        {exported !== null && (
          <Alert type="success" message={`Exported ${exported} record(s) successfully.`} showIcon />
        )}

        <Button
          type="primary"
          icon={<DownloadOutlined />}
          size="large"
          loading={exporting}
          onClick={handleExport}
          block
        >
          {exporting ? 'Exporting...' : 'Export CSV'}
        </Button>

      </Space>
    </div>
  );
}
